import React, { useState, useEffect } from 'react';
import { Typography, Box, Tabs, Tab, Chip, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, TextField, FormControl, InputLabel, Select } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AIApiKeys from './AIApiKeys';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { toastSuccess, toastError } from '../utils/toast';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { ApiTypes } from '@flashbacktech/flashbackclient/api';

// Component pour Storage API Keys
const StorageAPIKeysSection: React.FC = () => {
  const { authState, globalData } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const [repos, setRepos] = useState<any[]>([]);
  const [selectedRepoId, setSelectedRepoId] = useState<string>('ALL');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedKeyForMenu, setSelectedKeyForMenu] = useState<any | null>(null);

  // Edit Modal
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingKey, setEditingKey] = useState<any | null>(null);
  const [editName, setEditName] = useState('');

  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<any | null>(null);

  // Create API Key Modal
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyAccessType, setNewKeyAccessType] = useState<ApiTypes.AccessType>(ApiTypes.AccessType.READ);
  const [selectedRepoForCreate, setSelectedRepoForCreate] = useState<string>('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [createdKey, setCreatedKey] = useState<{ key: string; secret: string } | null>(null);
  const [showCreatedSecret, setShowCreatedSecret] = useState(false);

  // Use centralized workspace hook
  const selectedWorkspaceId = useSelectedWorkspace();

  // Load repos when workspace changes
  useEffect(() => {
    loadRepos();
  }, [selectedWorkspaceId, globalData?.workspaces]);

  // Listen for reload event from parent
  useEffect(() => {
    const handleReload = () => { loadRepos(); };
    window.addEventListener('reloadApiKeysData', handleReload);
    return () => window.removeEventListener('reloadApiKeysData', handleReload);
  }, [selectedWorkspaceId, globalData?.workspaces]);

  const loadRepos = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://backend.flashback.tech';
      
      console.log('[StorageAPIKeys] Loading repos for workspace:', selectedWorkspaceId);
      
      // Si ALL, charger les repos de tous les workspaces accessibles
      if (selectedWorkspaceId === 'ALL') {
        if (!globalData?.workspaces || globalData.workspaces.length === 0) {
          console.log('[StorageAPIKeys] No workspaces available');
          setRepos([]);
          return;
        }
        
        // Charger les repos de tous les workspaces
        const allRepos: any[] = [];
        for (const workspace of globalData.workspaces) {
          try {
            const response = (await withTokenRefresh(() => apiClient.getStorageRepos(workspace.id))) as any;
            if (response.success && response.repos) {
              allRepos.push(...response.repos);
            }
          } catch (err) {
            console.error(`[StorageAPIKeys] Error loading repos for workspace ${workspace.id}:`, err);
          }
        }
        
        console.log('[StorageAPIKeys] Loaded repos (ALL workspaces):', allRepos.length);
        setRepos(allRepos);
      } else if (selectedWorkspaceId) {
        // Charger les repos du workspace sélectionné
        const response = (await withTokenRefresh(() => apiClient.getStorageRepos(selectedWorkspaceId))) as any;
        if (response.success && response.repos) {
          setRepos(response.repos);
        }
      } else {
        console.log('[StorageAPIKeys] No workspace selected');
        setRepos([]);
      }
    } catch (error) {
      console.error('[StorageAPIKeys] Error loading repos:', error);
      toastError('Failed to load repositories');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toastSuccess('Copied to clipboard');
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, apiKey: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedKeyForMenu(apiKey);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedKeyForMenu(null);
  };

  const handleEditApiKey = async () => {
    if (!editingKey) return;

    try {
      const response = (await withTokenRefresh(() => 
          apiClient.updateRepoKey(
            editingKey.repoId, 
            editingKey.id, 
            { name: editName, accessType: editingKey.accessType }))) as any;
      if (response.success) {
        toastSuccess('API key updated successfully');
        setOpenEditModal(false);
        setEditingKey(null);
        loadRepos(); // Reload to get updated data
      } else {
        toastError(response.message || 'Failed to update API key');
      }
    } catch (error: any) {
      console.error('[StorageAPIKeys] Error updating API key:', error);
      toastError(error.message || 'Failed to update API key');
    }
  };

  const handleDeleteApiKey = async () => {
    if (!keyToDelete) return;

    try {
      const response = (await withTokenRefresh(() => apiClient.deleteRepoKey(keyToDelete.repoId, keyToDelete.id))) as any;

      const data = await response.json();
      if (data.success || response.ok) {
        toastSuccess('API key deleted successfully');
        setDeleteDialogOpen(false);
        setKeyToDelete(null);
        loadRepos(); // Reload to get updated data
      } else {
        toastError(data.message || 'Failed to delete API key');
      }
    } catch (error: any) {
      console.error('[StorageAPIKeys] Error deleting API key:', error);
      toastError(error.message || 'Failed to delete API key');
    }
  };

  const handleCreateApiKey = async () => {
    if (!selectedRepoForCreate || !newKeyName.trim()) {
      toastError('Please select a repository and enter a name');
      return;
    }

    setCreatingKey(true);
    try {
      const createRepoKeyRequest: ApiTypes.CreateRepoKeyRequest = {
        repoId: selectedRepoForCreate,
        name: newKeyName.trim(),
        accessType: newKeyAccessType,
      };
      const response = (await withTokenRefresh(() =>
        apiClient.createRepoKey(createRepoKeyRequest)
      )) as any;

      if (response.success) {
        toastSuccess('API key created successfully');
        setCreatedKey({ key: response.key, secret: response.secret || '' });
        loadRepos(); // Reload to get updated data
        // Dispatch event for tutorial
        window.dispatchEvent(new CustomEvent('repoApiKeyCreated'));
      } else {
        toastError(response.message || 'Failed to create API key');
      }
    } catch (error: any) {
      console.error('[StorageAPIKeys] Error creating API key:', error);
      toastError(error.message || 'Failed to create API key');
    } finally {
      setCreatingKey(false);
    }
  };

  const resetCreateModal = () => {
    setOpenCreateModal(false);
    setNewKeyName('');
    setNewKeyAccessType(ApiTypes.AccessType.READ);
    setSelectedRepoForCreate('');
    setCreatedKey(null);
    setShowCreatedSecret(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Filtrer les repos par recherche
  const filteredRepos = repos.filter((repo) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return repo.name.toLowerCase().includes(query);
  });

  // Filtrer par repo sélectionné
  const displayRepos = selectedRepoId === 'ALL' ? filteredRepos : filteredRepos.filter((r) => r.id === selectedRepoId);

  // Compter le total d'API keys
  const totalApiKeys = displayRepos.reduce((sum, repo) => sum + (repo.apiKeys?.length || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Typography
            variant="body1"
            style={{
              fontSize: '14px',
              lineHeight: '20px',
              color: '#535862',
              fontWeight: 400,
            }}
          >
            Manage API keys for your storage resources
          </Typography>
        </div>

        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              border: '1px solid #D1D5DB',
            }}
            title="Search"
          >
            <SearchIcon sx={{ fontSize: 20 }} />
          </button>
          <button
            data-tutorial="generate-key"
            onClick={() => {
              setSelectedRepoForCreate(repos.length > 0 ? repos[0].id : '');
              setOpenCreateModal(true);
            }}
            className="flex items-center justify-center gap-2 text-white hover:opacity-90 transition-colors"
            style={{
              height: '40px',
              padding: '0 16px',
              borderRadius: '8px',
              backgroundColor: '#617BFF',
              fontWeight: 600,
              fontSize: '14px',
            }}
            disabled={repos.length === 0}
            title={repos.length === 0 ? 'Create a repository first' : 'Create API Key'}
          >
            <AddIcon sx={{ fontSize: 18 }} />
            Create API Key
          </button>
        </div>
      </div>

      {/* Search Bar */}
      {showSearch && (
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search repositories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{
              fontSize: '14px',
            }}
          />
        </div>
      )}

      <div className="border-b border-gray-200 mb-6"></div>

      {/* Repository Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
          Filter by Repository
        </label>
        <select
          value={selectedRepoId}
          onChange={(e) => setSelectedRepoId(e.target.value)}
          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{ fontSize: '14px' }}
        >
          <option value="ALL">All Repositories ({repos.length})</option>
          {repos.map((repo) => (
            <option key={repo.id} value={repo.id}>
              {repo.name} ({repo.apiKeys?.length || 0} keys)
            </option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="mb-4">
        <Typography style={{ fontSize: '13px', color: '#667085' }}>
          {totalApiKeys} API {totalApiKeys === 1 ? 'key' : 'keys'} found in {displayRepos.length} {displayRepos.length === 1 ? 'repository' : 'repositories'}
        </Typography>
      </div>

      {/* Repositories List with API Keys */}
      {displayRepos.length === 0 ? (
        <div className="text-center py-12">
          <Typography style={{ color: '#667085', fontSize: '14px' }}>
            {searchQuery
              ? 'No repositories found matching your search'
              : 'No repositories yet.'}
          </Typography>
        </div>
      ) : (
        <div className="space-y-6">
          {displayRepos.map((repo) => (
            <div key={repo.id} className="bg-white border border-gray-200 rounded-lg p-4">
              {/* Repo Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontSize: '16px',
                      color: '#101828',
                    }}
                  >
                    {repo.name}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '12px',
                      color: '#667085',
                      marginTop: '2px',
                    }}
                  >
                    {repo.apiKeys?.length || 0} API {(repo.apiKeys?.length || 0) === 1 ? 'key' : 'keys'}
                  </Typography>
                </div>
                <Chip
                  label={repo.storageType || 'S3'}
                  size="small"
                  style={{
                    backgroundColor: '#F0F2FF',
                    color: '#617BFF',
                    border: '1px solid #D6DDFF',
                    fontWeight: 600,
                    fontSize: '12px',
                  }}
                />
              </div>

              {/* API Keys for this repo */}
              {repo.apiKeys && repo.apiKeys.length > 0 ? (
                <div className="space-y-3">
                  {repo.apiKeys.map((apiKey: any) => {
                    // Add repoId to apiKey for edit/delete operations
                    const apiKeyWithRepo = { ...apiKey, repoId: repo.id };
                    
                    return (
                      <div
                        key={apiKey.id}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Chip
                                label={apiKey.accessType || 'READ'}
                                size="small"
                                style={{
                                  backgroundColor:
                                    apiKey.accessType === 'ADMIN'
                                      ? '#F3E8FF'
                                      : apiKey.accessType === 'WRITE'
                                      ? '#FEF3F2'
                                      : '#EAF8F1',
                                  color:
                                    apiKey.accessType === 'ADMIN'
                                      ? '#7C3AED'
                                      : apiKey.accessType === 'WRITE'
                                      ? '#B42318'
                                      : '#1F8F6B',
                                  border: `1px solid ${
                                    apiKey.accessType === 'ADMIN'
                                      ? '#DDD6FE'
                                      : apiKey.accessType === 'WRITE'
                                      ? '#FECDCA'
                                      : '#D1FAE5'
                                  }`,
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  height: '20px',
                                }}
                              />
                              <Typography
                                style={{
                                  fontSize: '12px',
                                  color: '#667085',
                                }}
                              >
                                Created {formatDate(apiKey.createdAt)}
                              </Typography>
                            </div>

                            <Typography
                              style={{
                                fontWeight: 600,
                                fontSize: '14px',
                                color: '#101828',
                                marginBottom: '8px',
                              }}
                            >
                              {apiKey.name}
                            </Typography>

                            {/* API Key */}
                            <div className="flex items-center gap-2">
                              <code
                                className="px-2 py-1 rounded text-sm font-mono"
                                style={{
                                  backgroundColor: '#FFFFFF',
                                  color: '#374151',
                                  border: '1px solid #E5E7EB',
                                  fontSize: '12px',
                                }}
                              >
                                {apiKey.key}
                              </code>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(apiKey.key)}
                                title="Copy key"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </div>
                          </div>

                          <IconButton
                            onClick={(e) => handleMenuClick(e, apiKeyWithRepo)}
                            size="small"
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Typography style={{ color: '#98A2B3', fontSize: '13px' }}>
                    No API keys for this repository yet
                  </Typography>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (selectedKeyForMenu) {
              setEditingKey(selectedKeyForMenu);
              setEditName(selectedKeyForMenu.name);
              setOpenEditModal(true);
            }
            handleMenuClose();
          }}
        >
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setKeyToDelete(selectedKeyForMenu);
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
          style={{ color: '#F04438' }}
        >
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Modal */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit API Key</DialogTitle>
        <DialogContent>
          <div className="pt-2">
            <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
              Name
            </label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ fontSize: '14px' }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setOpenEditModal(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleEditApiKey}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: '#617BFF' }}
          >
            Update
          </button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete API Key</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the API key "{keyToDelete?.name}"? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setDeleteDialogOpen(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteApiKey}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: '#F04438' }}
          >
            Delete
          </button>
        </DialogActions>
      </Dialog>

      {/* Create API Key Modal */}
      <Dialog 
        open={openCreateModal} 
        onClose={() => !creatingKey && resetCreateModal()} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '18px', color: '#101828' }}>
          {createdKey ? 'API Key Created' : 'Create API Key'}
        </DialogTitle>
        <DialogContent>
          {createdKey ? (
            <div className="space-y-4 pt-2">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <Typography sx={{ fontSize: '14px', color: '#166534', fontWeight: 500, mb: 1 }}>
                  Your API key has been created successfully!
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#166534' }}>
                  Make sure to copy the secret now. You won't be able to see it again.
                </Typography>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
                  API Key
                </label>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-mono"
                    style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
                  >
                    {createdKey.key}
                  </code>
                  <IconButton size="small" onClick={() => copyToClipboard(createdKey.key)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
                  Secret
                </label>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-mono"
                    style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
                  >
                    {showCreatedSecret ? createdKey.secret : '••••••••••••••••••••••••'}
                  </code>
                  <IconButton size="small" onClick={() => setShowCreatedSecret(!showCreatedSecret)}>
                    {showCreatedSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                  <IconButton size="small" onClick={() => copyToClipboard(createdKey.secret)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
                  Repository *
                </label>
                <select
                  value={selectedRepoForCreate}
                  onChange={(e) => setSelectedRepoForCreate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ fontSize: '14px' }}
                  data-tutorial="select-repo"
                >
                  <option value="">Select a repository</option>
                  {repos.map((repo) => (
                    <option key={repo.id} value={repo.id}>
                      {repo.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Enter a name for this API key"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ fontSize: '14px' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
                  Access Type
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setNewKeyAccessType(ApiTypes.AccessType.READ)}
                    className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      newKeyAccessType === ApiTypes.AccessType.READ
                        ? 'bg-green-50 border-green-300 text-green-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Read
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewKeyAccessType(ApiTypes.AccessType.WRITE)}
                    className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      newKeyAccessType === ApiTypes.AccessType.WRITE
                        ? 'bg-orange-50 border-orange-300 text-orange-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Write
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewKeyAccessType(ApiTypes.AccessType.ADMIN)}
                    className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      newKeyAccessType === ApiTypes.AccessType.ADMIN
                        ? 'bg-purple-50 border-purple-300 text-purple-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Admin
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {newKeyAccessType === ApiTypes.AccessType.READ && 'Read-only access to repository data'}
                  {newKeyAccessType === ApiTypes.AccessType.WRITE && 'Write access to repository data'}
                  {newKeyAccessType === ApiTypes.AccessType.ADMIN && 'Full admin access (read and write)'}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          {createdKey ? (
            <button
              onClick={resetCreateModal}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg"
              style={{ backgroundColor: '#617BFF' }}
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={resetCreateModal}
                disabled={creatingKey}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateApiKey}
                disabled={creatingKey || !selectedRepoForCreate || !newKeyName.trim()}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50"
                style={{ backgroundColor: '#617BFF' }}
              >
                {creatingKey ? 'Creating...' : 'Create API Key'}
              </button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`api-keys-tabpanel-${index}`}
      aria-labelledby={`api-keys-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function APIKeys() {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Repositories
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Manage your storage repositories and their configurations
        </Typography>
      </div>

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">

      {/* API Keys Sub-section Header with buttons */}
      <Box sx={{ ml: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
              API Keys
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#667085' }}>
              Manage all your API keys for resources and AI LLMs
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Reload */}
            <Tooltip title="Reload">
              <IconButton
                size="small"
                onClick={() => { window.dispatchEvent(new CustomEvent('reloadApiKeysData')); }}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="API keys tabs"
          data-tutorial="api-keys-tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              minWidth: 120,
              color: '#717680',
              '&.Mui-selected': {
                color: '#617BFF',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#617BFF',
              height: 2,
            },
          }}
        >
          <Tab label="Buckets" />
          <Tab label="AI LLMs" data-tutorial="tab-ai-llms" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <StorageAPIKeysSection />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <AIApiKeys />
      </TabPanel>
      </div>
    </div>
  );
}

export default APIKeys;
